import { useState, useEffect } from 'react'
import axios, { AxiosResponse, AxiosError } from 'axios'
import CryptoJS from 'crypto-js'

interface ApiResponse {
    // Define the structure of the API response here
    // Example: Assuming the response is an array of objects with 'id' and 'name' properties
    success: string
    data: Object
    msg: string
}

interface ApiError {
    // Define the structure of the API error response here
    // Example: Assuming the error response has 'message' property
    msg: string
}

interface UseGetApiResult {
    data: ApiResponse[] | null
    loading: boolean
    error: ApiError | null
    setEnabled: React.Dispatch<React.SetStateAction<boolean>>
}

const CRYPTO_KEY = CryptoJS.enc.Utf8.parse(
    process.env.REACT_APP_MOBILE_AES_KEY || "12345678901234567890123456789012"
);

export function decryptMobile(encryptedMobile: string, ivBase64: string): string {
    if (!encryptedMobile || !ivBase64) {
        return "";
    }
    try {
        const decrypted = CryptoJS.AES.decrypt(
            {
                ciphertext: CryptoJS.enc.Base64.parse(encryptedMobile),
            } as any,
            CRYPTO_KEY,
            {
                iv: CryptoJS.enc.Base64.parse(ivBase64),
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            }
        );
        return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (err) {
        console.error("Failed to decrypt mobile:", err);
        return "";
    }
}

const decodeBase64Mobile = (mobile: string): string => {
    try {
        if (mobile && typeof mobile === 'string') {
            const decoded = atob(mobile)
            if (/^\+?[0-9\s\-]+$/.test(decoded)) {
                return decoded
            }
        }
    } catch (e) {
        // Not base64 encoded or failed to decode
    }
    return mobile
}

const useGetApi = (
    url: string,
    token: string,
    enabledCall = true,
    Authorization?: string
): UseGetApiResult => {
    const [data, setData] = useState<ApiResponse[] | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<ApiError | null>(null)
    const [enabled, setEnabled] = useState<boolean>(enabledCall)

    useEffect(() => {
        if (enabled) {
            const fetchData = async () => {
                try {
                    const response: AxiosResponse<ApiResponse[]> = await axios.get(
                        url,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                token: token,
                                ...(Authorization && {Authorization : Authorization})
                            },
                        },
                    )
                    
                    let responseData: any = response.data
                    if (responseData && typeof responseData === 'object') {
                        if (responseData.data && typeof responseData.data === 'object') {
                            if (responseData.data.mobile_number && responseData.data.mobile_iv) {
                                responseData.data.mobile_number = decryptMobile(
                                    responseData.data.mobile_number,
                                    responseData.data.mobile_iv
                                )
                            } else if (responseData.data.mobile_number) {
                                responseData.data.mobile_number = decodeBase64Mobile(responseData.data.mobile_number)
                            }
                        }
                    }
                    setData(responseData)
                } catch (error: unknown) {
                    const apiError = (error as AxiosError<ApiError>).response
                        ?.data || {
                        msg: 'An error occurred while fetching the data.',
                    }
                    setError(apiError)
                } finally {
                    setLoading(false)
                }
            }

            fetchData()
            setEnabled(false)
        }
    }, [url, token, enabled])

    return { data, loading, error, setEnabled }
}

export default useGetApi
